// Shared rent-row maths for monthly leasing calculations.
//
// SINGLE SOURCE OF TRUTH for "how much rent does a period cost?".
// The behaviour here is lifted verbatim from MonthlyCalculator.js (the manual
// per-contract editor), which is the authoritative implementation. Both the
// manual editor and the bulk sync (contractsSync.js) must use these helpers so
// that the same period always produces the same rent, no matter which code path
// created the row.
//
// Rules (the owner's monthly rule, 26 Sep 2026 - the calculator, the bulk sync, the page's live balance and
// the engine's Python twin, fines_pipeline/monthly_fill.py, all follow it):
//   - Days are counted INCLUSIVELY (pickup day counts as day 1).
//   - A month is 30 days: a period of 30 days or more is billed at the full monthly amount (it was 28).
//   - A shorter period is prorated on a fixed 30-day month:
//       (baseRate / 30) * days, rounded to 2 decimals.
//   - The FIRST month (`first: true` - the contract's first row) is always the full monthly amount, even
//     when the contract is closed before the month is over.
//
// This module is pure: no React, no Firestore, no side effects.

import { parseSheetDate } from './dates';
import { withoutVat } from '../../../constants/finance';

/**
 * The note on a short contract's first month priced at Ejar's own rent for its days (no monthly rate at all - owner,
 * 14 Oct 2026). Such a month is never a rate: nothing learns a monthly rate from it (contract_staff.SHORT_RENT_NOTE).
 */
export const SHORT_RENT_NOTE = "Ejar's rent for its days (no monthly rate)";
const isShortRentRow = (row) => !!row && row.rateNote === SHORT_RENT_NOTE;

/** Fixed month length used for the daily rate. */
export const FULL_MONTH_DAYS = 30;

/** A period of at least this many days is billed as a full month (a month is 30 days - owner). */
export const FULL_PERIOD_MIN_DAYS = 30;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Mirrors parseFormattedNumber in MonthlyCalculator.js / parseNumber in
 * contractsSync.js: tolerant number parsing that strips thousands separators.
 */
export const parseAmount = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
    const parsed = parseFloat(String(val).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (num) => parseFloat(num.toFixed(2));

/**
 * Accepts a Date or a sheet date string ("DD/MM/YYYY" / "DD-MM-YYYY") and
 * returns a NEW Date normalised to UTC midnight, or null. Never mutates input.
 */
const toUtcMidnight = (value) => {
    if (!value) return null;
    let date;
    if (value instanceof Date) {
        date = new Date(value.getTime());
    } else {
        date = parseSheetDate(String(value));
    }
    if (!date || isNaN(date.getTime())) return null;
    date.setUTCHours(0, 0, 0, 0);
    return date;
};

/**
 * Inclusive day count of a period (pickup day counts as day 1).
 * Returns 0 when either end of the period cannot be parsed.
 */
export const inclusiveDays = (start, end) => {
    const from = toUtcMidnight(start);
    const to = toUtcMidnight(end);
    if (!from || !to) return 0;
    return Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY) + 1;
};

/**
 * Rent charged for a single period.
 *
 * @param {Object}        params
 * @param {Date|string}   params.start    Period start (inclusive).
 * @param {Date|string}   params.end      Period end (inclusive).
 * @param {number|string} params.baseRate Full monthly rent (VAT inclusive).
 * @param {boolean}       [params.first]  The contract's first month: always the full monthly amount.
 * @returns {number} Rent for the period; 0 when it cannot be determined.
 */
export const rentForPeriod = ({ start, end, baseRate, first = false }) => {
    const rate = parseAmount(baseRate);
    if (rate === 0) return 0;

    const days = inclusiveDays(start, end);
    if (days <= 0) return 0;

    // Full month: 30+ days, or the first month whatever its length.
    if (first || days >= FULL_PERIOD_MIN_DAYS) return rate;

    // Partial period: daily rate based on a fixed 30-day month.
    return round2((rate / FULL_MONTH_DAYS) * days);
};

/**
 * Rent for a period split into the VAT-inclusive total, the net rent and the
 * VAT amount, using the existing 5% VAT arithmetic (total / 1.05).
 * VAT handling is intentionally identical to the inline code it replaces.
 */
export const rentRowAmounts = ({ start, end, baseRate, first = false }) => {
    const totalRent = rentForPeriod({ start, end, baseRate, first });
    const rent = round2(withoutVat(totalRent));
    const vat = round2(totalRent - rent);
    return { totalRent, rent, vat };
};

// ---------------------------------------------------------------------------
// Rate schedule: "from <date> the monthly rate is <rate>". A contract whose price
// changed mid-way is no longer a hand-edited row - it is a dated fact, and every
// period is priced from it with the SAME rules as above.
// ---------------------------------------------------------------------------

const fmtDDMMYYYY = (date) => {
    const d = toUtcMidnight(date);
    if (!d) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

/** Sorted copy of a schedule with parsed dates; entries without a date or rate are dropped. */
const normalizeSchedule = (schedule) => (Array.isArray(schedule) ? schedule : [])
    .map((e) => ({ ...e, _from: toUtcMidnight(e.from), rate: parseAmount(e.rate) }))
    .filter((e) => e._from && e.rate > 0)
    .sort((a, b) => a._from - b._from);

/** The days a month row bills: from its start up to its end (the day the next month starts) - 0 when unreadable. */
const billedDaysOf = (start, end) => {
    const from = toUtcMidnight(start);
    const to = toUtcMidnight(end);
    return from && to ? Math.round((to.getTime() - from.getTime()) / MS_PER_DAY) : 0;
};

/**
 * Learns the schedule from saved monthly rows: the rate of each FULL month (the first row whatever its length, or a
 * row billing 30+ days) - when it moves by more than 0.5 AED from the rate in force, a new entry starts at that row.
 * A shorter row (a returned contract's last month) is never learned from: its price is the rate / 30 x its days, and
 * learning a rate back from it made each open re-price it lower (review, 14 Oct 2026). Falls back to `fallbackBase`.
 */
export const scheduleFromRows = (rows, fallbackBase) => {
    const out = [];
    let current = 0;
    (rows || []).forEach((row, i) => {
        const from = toUtcMidnight(row?.start);
        if (!from) return;
        const total = parseAmount(row.totalRent) || (parseAmount(row.rent) + parseAmount(row.vat));
        const full = i === 0 || billedDaysOf(row.start, row.end) >= FULL_PERIOD_MIN_DAYS;
        if (total <= 0 || !full || isShortRentRow(row)) return;
        const implied = round2(total);
        if (out.length === 0 || Math.abs(implied - current) > 0.5) {
            out.push({
                from: fmtDDMMYYYY(from), rate: implied,
                source: out.length === 0 ? 'contract' : 'learned',
                note: out.length === 0 ? 'pick-up rate' : `changed from ${current}`
            });
            current = implied;
        }
    });
    if (out.length === 0 && parseAmount(fallbackBase) > 0) {
        const first = rows?.[0]?.start ? fmtDDMMYYYY(rows[0].start) : '';
        out.push({ from: first, rate: parseAmount(fallbackBase), source: 'contract', note: 'contract rate' });
    }
    return out;
};

/** The monthly rate in force on `date` (the last entry starting on/before it), else `fallback`. */
export const rateAt = (schedule, date, fallback = 0) => {
    const d = toUtcMidnight(date);
    const entries = normalizeSchedule(schedule);
    if (!d || entries.length === 0) return parseAmount(fallback);
    let rate = entries[0].rate;               // before the first dated entry: the first rate
    for (const e of entries) { if (e._from <= d) rate = e.rate; else break; }
    return rate;
};

/**
 * Rent for a period priced from the schedule. Without a change inside the period the
 * result is EXACTLY rentForPeriod() at the rate in force. With a change inside:
 *   - a full period (30+ days, or the first month) is ONE month at the NEW rate - company rule
 *     (2026-09-19, I2603000D03146973): a price agreed on the 20th applies to the
 *     whole billing month that contains it, no day-blending
 *   - a partial period keeps the 30-day proration per part:  Σ rate_i / 30 × days_i
 * Returns { total, parts:[{from,to,days,rate,amount}], split }.
 */
export const rentForPeriodSchedule = ({ start, end, schedule, fallbackBase, first = false }) => {
    const from = toUtcMidnight(start);
    const to = toUtcMidnight(end);
    const days = inclusiveDays(start, end);
    if (!from || !to || days <= 0) return { total: 0, parts: [], split: false };
    const entries = normalizeSchedule(schedule);
    if (entries.length === 0) {
        const total = rentForPeriod({ start, end, baseRate: fallbackBase, first });
        return { total, parts: [{ from: fmtDDMMYYYY(from), to: fmtDDMMYYYY(to), days, rate: parseAmount(fallbackBase), amount: total }], split: false };
    }
    // change dates strictly inside (from, to): a change ON the period's last day belongs to the NEXT month - a month
    // ends on the day the next one starts (08/05 -> 07/06, then 07/06 -> ...), and a comment written that day is the
    // next month's price (owner, 4 Oct 2026: I2603000D03131009 "1st month 1575, 2nd month 2000" - the 2,000 dated
    // the 2nd month's first day priced the 1st month too)
    const last = days > 1 ? new Date(to.getTime() - MS_PER_DAY) : to;
    const cuts = entries.map((e) => e._from).filter((d) => d > from && d <= last);
    if (cuts.length === 0) {
        const rate = rateAt(schedule, from, fallbackBase);
        const total = rentForPeriod({ start, end, baseRate: rate, first });
        return { total, parts: [{ from: fmtDDMMYYYY(from), to: fmtDDMMYYYY(to), days, rate, amount: total }], split: false };
    }
    if (first || days >= FULL_PERIOD_MIN_DAYS) {
        // full month with a change inside: the whole month at the rate in force at its END (its last day before the
        // next month starts) - the comment is written a few days into the month, when the money is taken (owner)
        const rate = rateAt(schedule, last, fallbackBase);
        const total = rentForPeriod({ start, end, baseRate: rate, first });
        return { total, parts: [{ from: fmtDDMMYYYY(from), to: fmtDDMMYYYY(to), days, rate, amount: total }], split: true };
    }
    const bounds = [from, ...cuts, new Date(to.getTime() + MS_PER_DAY)];
    const parts = [];
    for (let i = 0; i < bounds.length - 1; i++) {
        const pFrom = bounds[i];
        const pTo = new Date(bounds[i + 1].getTime() - MS_PER_DAY);
        const pDays = Math.floor((pTo.getTime() - pFrom.getTime()) / MS_PER_DAY) + 1;
        if (pDays <= 0) continue;
        const rate = rateAt(schedule, pFrom, fallbackBase);
        const amount = round2((rate / FULL_MONTH_DAYS) * pDays);   // partial period only (full months handled above)
        parts.push({ from: fmtDDMMYYYY(pFrom), to: fmtDDMMYYYY(pTo), days: pDays, rate, amount });
    }
    const total = round2(parts.reduce((a, p) => a + p.amount, 0));
    return { total, parts, split: true };
};

// ---------------------------------------------------------------------------
// The billed end of a returned contract (owner, 14 Oct 2026): a month ends on the day the next one starts
// (08/05 -> 07/06, then 07/06 -> ...), so that day is never counted twice; the days are Ejar's own count (its
// Trip Days, with its grace and extra-hour rules) - the calculator fixes only the price.
// ---------------------------------------------------------------------------

/**
 * The day after a returned contract's last billed day: the pick-up day + Ejar's Trip Days when Ejar counted them
 * after the return, else the drop-off day (the days elapsed, the boundary day once). null when neither is known.
 */
export const billedEndOf = ({ pickup, dropoff, ejarDays }) => {
    const from = toUtcMidnight(pickup);
    const days = parseAmount(ejarDays);
    if (from && days > 0 && Number.isInteger(days)) return new Date(from.getTime() + days * MS_PER_DAY);
    return toUtcMidnight(dropoff);
};

/**
 * Rent of a month row from its `start` up to `billEnd` (its end: the day the next month starts, or a returned
 * contract's billed end) - the days in between, the boundary day never twice:
 *   - 30 days or more, or the contract's first month = one month at the rate in force (a change inside the month:
 *     the rate at its last day, as rentForPeriodSchedule - a change dated on `billEnd` is the next month's)
 *   - fewer = rate / 30 x days, per part when the rate changes inside
 * A full month row (start -> start + 30) costs exactly what rentForPeriodSchedule gives it; only a shorter one differs
 * (it counted the boundary day twice: one day too many on every returned contract - audit 14 Oct 2026).
 * Returns { total, parts:[{from,to,days,rate,amount}], split }.
 */
export const rentForBilledPeriod = ({ start, billEnd, schedule, fallbackBase, first = false }) => {
    const from = toUtcMidnight(start);
    const to = toUtcMidnight(billEnd);
    if (!from || !to) return { total: 0, parts: [], split: false };
    const days = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
    if (days <= 0 && !first) return { total: 0, parts: [], split: false };
    const entries = normalizeSchedule(schedule);
    const rateOn = (d) => (entries.length ? rateAt(schedule, d, fallbackBase) : parseAmount(fallbackBase));
    const last = days > 0 ? new Date(to.getTime() - MS_PER_DAY) : from;          // the last billed day
    const cuts = entries.map((e) => e._from).filter((d) => d > from && d <= last);
    const whole = first || days >= FULL_PERIOD_MIN_DAYS;
    const one = (rate, amount) => ({ total: amount, parts: [{ from: fmtDDMMYYYY(from), to: fmtDDMMYYYY(last), days: Math.max(days, 0), rate, amount }], split: cuts.length > 0 });
    if (whole) {
        const rate = rateOn(cuts.length ? last : from);
        return one(rate, rate);
    }
    if (cuts.length === 0) {
        const rate = rateOn(from);
        return one(rate, round2((rate / FULL_MONTH_DAYS) * days));
    }
    const bounds = [from, ...cuts, to];
    const parts = [];
    for (let i = 0; i < bounds.length - 1; i++) {
        const pDays = Math.round((bounds[i + 1].getTime() - bounds[i].getTime()) / MS_PER_DAY);
        if (pDays <= 0) continue;
        const rate = rateOn(bounds[i]);
        parts.push({ from: fmtDDMMYYYY(bounds[i]), to: fmtDDMMYYYY(new Date(bounds[i + 1].getTime() - MS_PER_DAY)), days: pDays, rate,
                     amount: round2((rate / FULL_MONTH_DAYS) * pDays) });
    }
    return { total: round2(parts.reduce((a, p) => a + p.amount, 0)), parts, split: true };
};

/** rentForBilledPeriod's total. */
export const rentForBilledDays = (args) => rentForBilledPeriod(args).total;

/**
 * The monthly rate read from the contract's FIRST row - used as a fallback when the contract carries
 * no official monthly rate. The first month is always billed the full monthly amount (owner, 26 Sep
 * 2026), whatever its length, so its amount IS the monthly rate (it used to be scaled up to 30 days
 * when the row was shorter than 28 days).
 *
 * Returns 0 when the row has no usable amount or no parseable period.
 */
export const baseRateFromFirstRow = (row) => {
    if (!row || isShortRentRow(row)) return 0;
    const total = parseAmount(row.totalRent);
    if (total <= 0) return 0;

    // Unparseable period: not a real month row, so no base rate.
    if (!toUtcMidnight(row.start) || !toUtcMidnight(row.end)) return 0;
    return total;
};

/**
 * The monthly rate read from the saved rows (the fallback when the contract has no monthly amount): the
 * first FULL-length row (30+ days) - its amount is a whole month under any rule - else the first row's
 * amount (the first month is always full). Rows saved before 26 Sep 2026 could hold a first month PRORATED
 * on its days; a later full row still gives their real rate.
 */
export const baseRateFromRows = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const full = list.find((r) => r && !isShortRentRow(r) && parseAmount(r.totalRent) > 0 && billedDaysOf(r.start, r.end) >= FULL_PERIOD_MIN_DAYS);
    if (full) return parseAmount(full.totalRent);
    return baseRateFromFirstRow(list[0]);
};
